/* eslint-disable max-lines */
import {
  getFocusable,
  handleModalKeydown,
  setModalOpen
} from "components/supplier_admin/shared/modal_helpers";

const buildGangsheetsUrl = orderId => `/supplier/printer_queue/${orderId}/gangsheets`;

const DOWNLOAD_DELAY_MS = 500;

const SELECTORS = {
  modal: ".js-printer-queue-modal",
  modalClose: ".js-printer-queue-modal-close",
  modalDialog: ".js-printer-queue-modal-dialog",
  modalTitle: ".js-printer-queue-modal-title",
  modalSubtitle: ".js-printer-queue-modal-subtitle",
  modalStatus: ".js-printer-queue-modal-status",
  modalList: ".js-printer-queue-modal-list",
  downloadAll: ".js-printer-queue-download-all",
  gangsheetButton: ".js-printer-queue-gangsheet-button"
};

const formatInches = value => {
  if (value === null || typeof value === "undefined") return null;
  const numericValue = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(numericValue)) return null;
  const rounded = Math.round(numericValue * 10) / 10;
  return `${rounded}`;
};

const triggerDownload = url => {
  const $anchor = document.createElement("a");
  $anchor.href = url;
  $anchor.download = "";
  $anchor.rel = "noopener";
  document.body.appendChild($anchor);
  $anchor.click();
  document.body.removeChild($anchor);
};

const createGangsheetItemElement = sheet => {
  const width = formatInches(sheet.dimensions?.width);
  const height = formatInches(sheet.dimensions?.height);
  const sizeText = width && height ? `${width}x${height}IN` : "SIZE UNKNOWN";
  const label = `SHEET ${sheet.sheet_number} OF ${sheet.total_sheets} · ${sizeText}`;
  const filename = sheet.filename || "";
  const downloadUrl = sheet.download_url || "";

  const $itemEl = document.createElement("div");
  $itemEl.className = "printer-queue-modal__item";

  const $metaEl = document.createElement("div");
  $metaEl.className = "printer-queue-modal__meta";

  const $labelEl = document.createElement("div");
  $labelEl.className = "printer-queue-modal__label";
  $labelEl.textContent = label;

  const $filenameEl = document.createElement("div");
  $filenameEl.className = "printer-queue-modal__filename";
  $filenameEl.textContent = filename;

  $metaEl.appendChild($labelEl);
  $metaEl.appendChild($filenameEl);

  const $actionsEl = document.createElement("div");
  $actionsEl.className = "printer-queue-modal__actions";

  const $downloadEl = document.createElement("a");
  $downloadEl.className = "printer-queue-modal__download";
  $downloadEl.setAttribute("data-download-url", downloadUrl);
  $downloadEl.href = downloadUrl;
  $downloadEl.download = "";
  $downloadEl.rel = "noopener";
  $downloadEl.textContent = "Download";

  $actionsEl.appendChild($downloadEl);
  $itemEl.appendChild($metaEl);
  $itemEl.appendChild($actionsEl);
  return $itemEl;
};

class PrinterQueueGangsheetModal {
  #$container;

  #$modal;

  #gangsheetsCache = new Map();

  #state = { orderId: null, gangsheets: [], gangsheetsCount: 0, lastActiveElement: null };

  constructor($container) {
    this.#$container = $container;
    this.#$modal = document.querySelector(SELECTORS.modal);

    if (this.#$modal) {
      this.#bindEvents();
    }
  }

  get $modalList() {
    return this.#$modal?.querySelector(SELECTORS.modalList);
  }

  get $modalStatus() {
    return this.#$modal?.querySelector(SELECTORS.modalStatus);
  }

  get $modalTitle() {
    return this.#$modal?.querySelector(SELECTORS.modalTitle);
  }

  get $modalSubtitle() {
    return this.#$modal?.querySelector(SELECTORS.modalSubtitle);
  }

  get $downloadAllBtn() {
    return this.#$modal?.querySelector(SELECTORS.downloadAll);
  }

  get $modalDialog() {
    return this.#$modal?.querySelector(SELECTORS.modalDialog);
  }

  get $modalClose() {
    return this.#$modal?.querySelector(SELECTORS.modalClose);
  }

  #getFocusable() {
    return getFocusable(this.#$modal);
  }

  #setModalStatus(message) {
    const $statusEl = this.$modalStatus;
    if ($statusEl) {
      $statusEl.textContent = message || "";
    }
  }

  #setModalTitle(orderNumber) {
    const $titleEl = this.$modalTitle;
    if ($titleEl) {
      $titleEl.textContent = orderNumber ? `Gangsheets for ${orderNumber}` : "Gangsheets";
    }
  }

  #setModalSubtitle(count) {
    const $subtitleEl = this.$modalSubtitle;
    if (!$subtitleEl) return;
    if (!count) {
      $subtitleEl.textContent = "";
      return;
    }
    $subtitleEl.textContent = `${count} SHEET${count === 1 ? "" : "S"} ASSOCIATED WITH THIS ORDER`;
  }

  #setDownloadAllLabel(count) {
    const { $downloadAllBtn } = this;
    if (!$downloadAllBtn) return;
    $downloadAllBtn.textContent = count ? `Download All (${count})` : "Download All";
  }

  #renderLoadingSkeleton(count) {
    const { $modalList: $listEl, $downloadAllBtn } = this;
    if (!$listEl) return;
    if ($downloadAllBtn) $downloadAllBtn.disabled = true;
    this.#setModalSubtitle(0);
    this.#setDownloadAllLabel(0);
    this.#setModalStatus("");

    const placeholderCount = Math.max(1, Math.min(Number(count) || 2, 4));
    $listEl.innerHTML = Array.from({ length: placeholderCount })
      .map(
        () => `
          <div class="printer-queue-modal__item printer-queue-modal__item--skeleton">
            <div class="printer-queue-modal__skeleton-line printer-queue-modal__skeleton-line--short"></div>
            <div class="printer-queue-modal__skeleton-line"></div>
          </div>
        `
      )
      .join("");
  }

  #renderGangsheetsEmpty() {
    const { $modalList: $listEl, $downloadAllBtn } = this;
    if (!$listEl) return;
    $listEl.innerHTML = "";
    this.#setModalStatus("No gangsheets are available for this order.");
    if ($downloadAllBtn) $downloadAllBtn.disabled = true;
    this.#setModalSubtitle(0);
    this.#setDownloadAllLabel(0);
  }

  #renderGangsheets(gangsheets) {
    const { $modalList: $listEl, $downloadAllBtn } = this;
    if (!$listEl) return;

    if (!gangsheets || gangsheets.length === 0) {
      this.#renderGangsheetsEmpty();
      return;
    }

    if ($downloadAllBtn) $downloadAllBtn.disabled = false;
    this.#setModalStatus("");
    this.#setModalSubtitle(gangsheets.length);
    this.#setDownloadAllLabel(gangsheets.length);
    $listEl.innerHTML = "";
    const fragment = document.createDocumentFragment();
    gangsheets.forEach(sheet => {
      fragment.appendChild(createGangsheetItemElement(sheet));
    });
    $listEl.appendChild(fragment);
  }

  #downloadAll(orderId) {
    if (!this.#state.gangsheets || this.#state.gangsheets.length === 0) return;
    const $statusEl = this.$modalStatus;
    const isActiveOrder = () =>
      this.#state.orderId === orderId && this.#$modal?.classList.contains("is-open");
    let index = 0;
    const next = () => {
      if (!isActiveOrder()) return;
      const sheet = this.#state.gangsheets[index];
      if (!sheet) return;
      triggerDownload(sheet.download_url);
      index += 1;
      if ($statusEl) {
        $statusEl.textContent = `Downloading ${index} of ${this.#state.gangsheets.length}...`;
      }
      if (index < this.#state.gangsheets.length) {
        setTimeout(next, DOWNLOAD_DELAY_MS);
      } else if ($statusEl) {
        $statusEl.textContent = "Downloads started. Check your browser's download tray.";
      }
    };
    next();
  }

  #prepareModalForOpen(orderId, orderNumber) {
    this.#state.lastActiveElement = document.activeElement;
    this.#state.orderId = orderId;
    this.#setModalTitle(orderNumber);
    this.#renderLoadingSkeleton(this.#state.gangsheetsCount);
    this.#setModalStatus("Loading gangsheets...");
    setModalOpen({ modalEl: this.#$modal, isOpen: true, busyOnOpen: true });

    const [$firstFocusable] = this.#getFocusable();
    const $focusTarget = this.$modalDialog || this.$modalClose || $firstFocusable;
    $focusTarget?.focus();
  }

  #applyCachedGangsheets(orderId) {
    if (!this.#gangsheetsCache.has(orderId)) return false;
    const isActiveOrder = () => this.#state.orderId === orderId;
    const cached = this.#gangsheetsCache.get(orderId);
    this.#state.gangsheets = cached;
    if (isActiveOrder()) {
      this.#$modal.setAttribute("aria-busy", "false");
      this.#renderGangsheets(cached);
    }
    return true;
  }

  #fetchGangsheets(orderId) {
    const isActiveOrder = () => this.#state.orderId === orderId;

    fetch(buildGangsheetsUrl(orderId), { headers: { Accept: "application/json" } })
      .then(response => {
        if (!response.ok) throw new Error("Gangsheet request failed.");
        return response.json();
      })
      .then(payload => {
        if (!isActiveOrder()) return;
        const gangsheets = payload?.gangsheets || [];
        this.#gangsheetsCache.set(orderId, gangsheets);
        this.#state.gangsheets = gangsheets;
        this.#$modal.setAttribute("aria-busy", "false");
        this.#renderGangsheets(gangsheets);
      })
      .catch(() => {
        if (!isActiveOrder()) return;
        const { $modalList: $listEl, $downloadAllBtn } = this;
        this.#state.gangsheets = [];
        this.#$modal.setAttribute("aria-busy", "false");
        if ($listEl) $listEl.innerHTML = "";
        if ($downloadAllBtn) $downloadAllBtn.disabled = true;
        this.#setModalSubtitle(0);
        this.#setDownloadAllLabel(0);
        this.#setModalStatus("Failed to load gangsheets.");
      });
  }

  #openModal({ orderId, orderNumber }) {
    if (!this.#$modal) return;
    this.#prepareModalForOpen(orderId, orderNumber);
    if (this.#applyCachedGangsheets(orderId)) return;
    this.#fetchGangsheets(orderId);
  }

  #closeModal() {
    if (!this.#$modal) return;
    setModalOpen({ modalEl: this.#$modal, isOpen: false, busyOnOpen: true });
    const $fallback = this.#state?.lastActiveElement;
    if ($fallback && typeof $fallback.focus === "function") {
      $fallback.focus();
    }
  }

  #bindEvents() {
    this.#$container.addEventListener("click", event => {
      const $button = event.target.closest(SELECTORS.gangsheetButton);
      if (!$button) return;
      const $card = $button.closest(".printer-queue-card");
      const orderId = $card?.dataset.printerQueueOrderId;
      if (!orderId) return;
      const orderNumber = $card?.dataset.printerQueueOrderNumber;
      const gangsheetsCount = $card?.dataset.printerQueueGangsheetCount;
      this.#state.gangsheetsCount = Number(gangsheetsCount) || 0;
      this.#openModal({ orderId, orderNumber });
    });

    this.#$modal?.addEventListener("click", event => {
      if (event.target.closest(SELECTORS.modalClose)) {
        this.#closeModal();
      }
    });

    this.#$modal?.addEventListener("keydown", event =>
      handleModalKeydown(event, {
        getFocusable: this.#getFocusable.bind(this),
        dialogEl: this.$modalDialog,
        onEscape: () => this.#closeModal(),
        allowEscape: true
      })
    );

    this.$downloadAllBtn?.addEventListener("click", () => {
      this.#downloadAll(this.#state.orderId);
    });
  }
}

const initGangsheetModal = $container => {
  if (!$container) return;
  // eslint-disable-next-line no-new
  new PrinterQueueGangsheetModal($container);
};

export default initGangsheetModal;
